import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/Users/asaini/infra/dsmtop_wiki/db2-dsmtop-wiki/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component '" + name + "' was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h2>Purpose</h2>
    <p>Find the connections (sessions) that have consumed the most resources over their lifetime.  This view is helpful for diagnosing problems where large numbers of small activities in aggregate are consuming excessive resources.  A common scenario of this type is applications loading data one row at a time using singleton inserts.  This can be very disruptive but each individual insert is tiny and so is the singleton transaction associated with it.  This view will highlight connections engaged in these sort of activities.</p>
    <p>Note that this overlaps somewhat in functionality with the <a href="/developerworks/community/wikis/home?lang=en#!/wiki/Text%20Based%20Monitoring%20for%20DB2%20LUW%20-%20dsmtop/page/Sessions%20%28connections%29">Sessions (connections)</a> view.  If you go to the connections view, in counter mode, you could select various columns one at a time and sort by them to find the connections that have consumed the most CPU, read the most rows, etc.  This view provides a convenient way to get similar information in one step.</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1107px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "59.02777777777778%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAIAAADtbgqsAAAACXBIWXMAAAsSAAALEgHS3X78AAAB2UlEQVQoz1VRy27bMBDU/xQwkK9oT/2Jpu25aQvHcmw5sQMDKeIEaYHWtyiyZIkySUmkKOrl+Oyv6phqgnqwIKTlzO5w13Ic7+v369OPd1/Obs6+ze3h3XB4P7AXCNtejMcPV1e/HecXot//MRgsRqMH277v939eXPyxCNG6FFmaKyWKQmidV5VKU0bIumn0p88fer03Jye929ub3a4FpywVOGWps4xbcbzd7bZA2yLapmm32+csE563atvnt+/fWQaj0Xi/39d1DQIO8KWUVhRVUqZCyDxHvVKIjAGcCSE4567rPrqPvu8zysBG0aLQVVWhDQjo3HAW+ys/y7KiKMCIABIlSbIGgnVZglyFYRgEARK4VEr9E282Lecb5JGCmbquNnGMgBHG6JPraq1RNPD94IA1Ar9HYkJipEqDg2vOlSqUytEZVXIpSWQMERIdJqxfbbeUEu/Jg6DTM0rx3Wkg1pis1lEYGn1IjsUNO4hXnCed7dwADBRK01RilDIXBtKcyB+JV56Pl2JatUHTNNULwOuSZp2tWefLqvBmrIpShm6g4gKF4b8r8XoCpfHfzQ9MmLJcF5aS1ADrQb3lcjmbzZL/gCtK6XQ6nUwu5/PLyaTvOOfOZPAXwkvdVfH8jfoAAAAASUVORK5CYII=')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": "TopConsumers-Connections.png",
          "title": "TopConsumers-Connections.png",
          "src": "/db2-dsmtop-wiki/static/26702eaf504b7817f408165aac7df13f/917f9/TopConsumers-Connections.png",
          "srcSet": ["/db2-dsmtop-wiki/static/26702eaf504b7817f408165aac7df13f/7fc1e/TopConsumers-Connections.png 288w", "/db2-dsmtop-wiki/static/26702eaf504b7817f408165aac7df13f/a5df1/TopConsumers-Connections.png 576w", "/db2-dsmtop-wiki/static/26702eaf504b7817f408165aac7df13f/917f9/TopConsumers-Connections.png 1107w"],
          "sizes": "(max-width: 1107px) 100vw, 1107px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span></p>
    <h2>Histograms and Summaries (Gauges)</h2>
    <p>&lt;link TBD&gt;</p>
    <h2>Metrics Shown</h2>
    <p>There is a grid, with a row for each resource that is tracked.  Each row names the resource and shows</p>
    <ul><li>Item no, so that the user can use sort to restore the grid to its canonical ordering if it has been sorted on some other column.</li><li>Server resource (CPU, Rows Read, etc.)</li><li>The application handle for the connection that has consumed the most of this resource.</li><li>What percentage of the resource this particular connection has consumed, relative to consumption by other connections.</li><li>How many rows read, CPU seconds used, etc.</li><li>The name of the application associated with this connection.</li><li>SQL text of most recently executed statement, if available.</li></ul>
    <p><u>0  CPU Time</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.total_cpu_time</span></p>
    <p><br />
      <u>1  Rows Read</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.rows_read</span></p>
    <p><br />
      <u>2  Rows Returned</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.rows_returned</span></p>
    <p><br />
      <u>3  Rows Written</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.rows_modified</span></p>
    <p><br />
      <u>4  Logical Reads</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a></p>
    <p style={{
      "marginBottom": "5px",
      "marginLeft": "40px",
      "paddingTop": "2px",
      "paddingBottom": "2px",
      "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
    }}><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.admin.mon.doc/doc/r0001235.html?cp=SSEPGG_10.5.0&amp;lang=en" style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>pool_data_l_reads</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}> + </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.admin.mon.doc/doc/r0001238.html?lang=en" style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>pool_index_l_reads</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}> + </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.admin.mon.doc/doc/r0022731.html?lang=en" style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>pool_xda_l_reads</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}> + <a href="http://www.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.admin.mon.doc/doc/r0060763.html?lang=en">pool_col_l_reads</a> +</span></p>
    <p style={{
      "marginBottom": "5px",
      "marginLeft": "40px",
      "paddingTop": "2px",
      "paddingBottom": "2px",
      "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
    }}><a href="http://www.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.admin.mon.doc/doc/r0011302.html?lang=en">pool_temp_data_l_reads </a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace",
        "lineHeight": "1.5"
      }}>+ <a href="http://www.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.admin.mon.doc/doc/r0011303.html?cp=SSEPGG_10.5.0&amp;lang=en">pool_temp_index_l_reads </a>+ <a href="http://www.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.admin.mon.doc/doc/r0022738.html?cp=SSEPGG_10.5.0&amp;lang=en">pool_temp_xda_l_reads </a>+ <a href="http://www.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.admin.mon.doc/doc/r0060873.html?cp=SSEPGG_10.5.0&amp;lang=en">pool_temp_col_l_reads</a></span></p>
    <p><br />
      <u>5  IO r/w</u></p>
    <p><br />
      <u>6  Memory</u></p>
    <p><br />
      <u>7  Sort time</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.total_section_sort_time</span></p>
    <p><br />
      <u>8  Num sorts</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.total_sorts</span></p>
    <p><br />
      <u>9  Sort overflows</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.sort_overflows</span></p>
    <p><br />
      <u>10 Num threshold violations</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.thresh_violations</span></p>
    <p><br />
      <u>11 FCM Traffic</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a></p>
    <p style={{
      "marginLeft": "40px"
    }}><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>fcm_send_volume + fcm_recv_volume</span></p>
    <p><br />
      <u>12 Num agents</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.num_assoc_agents</span></p>
    <p><br />
      <u>13 Num locks held</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.num_locks_held</span></p>
    <p><br />
      <u>14 Lock Escalations</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.lock_escals</span></p>
    <p><br />
      <u>15 Lock timeouts</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.lock_timeouts</span></p>
    <p><br />
      <u>16 Lock Waits</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.lock_waits</span></p>
    <p><br />
      <u>17 Lock Wait Time</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.lock_wait_time</span></p>
    <p><br />
      <u>18 Lock Wait Time (Global)</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.lock_wait_time_global</span></p>
    <p><br />
      <u>19 Lock waits (global)</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.lock_waits_global</span></p>
    <p><br />
      <u>20 CF Wait Time</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.cf_wait_time</span></p>
    <p><br />
      <u>21 CF Waits</u></p>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Source: </span><a href="http://www-01.ibm.com/support/knowledgecenter/SSEPGG_10.5.0/com.ibm.db2.luw.sql.rtn.doc/doc/r0053938.html?cp=SSEPGG_10.5.0%2F3-6-1-3-10-22&amp;lang=en" style={{
        "textDecoration": "underline",
        "color": "rgb( 5 , 56 , 107 )",
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>mon_get_connection</a><span style={{
        "fontFamily": "'courier new' , 'courier' , monospace"
      }}>.cf_waits</span></p>
    <h2>Default Sort Column</h2>
    <p><span style={{
        "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
      }}>Item No, ascending</span></p>
    <h2>Navigation</h2>
    <p style={{
      "marginBottom": "5px",
      "paddingTop": "2px",
      "paddingBottom": "2px",
      "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
    }}>Keyboard navigation: VBc</p>
    <p style={{
      "marginBottom": "5px",
      "paddingTop": "2px",
      "paddingBottom": "2px",
      "fontFamily": "'helvetica neue' , 'helvetica' , 'arial' , sans-serif"
    }}>Dedicated shortcut key: (none)</p>
    <h6>Author: KevinLBeck</h6>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      